import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Form, Input, message, Button } from 'antd';
import {
  SafetyOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  StarFilled,
} from '@ant-design/icons';
import CustomerService from '../components/CustomerService';
import { BASE_URL } from '../utils/apiUrl';

const Landing = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [typedCode, setTypedCode] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [contactForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [footerLinks, setFooterLinks] = useState({
    developer: [],
    friendly: [],
    company: []
  });
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');
  const [heroBanner, setHeroBanner] = useState('');
  const [heroBannerLink, setHeroBannerLink] = useState('');
  const [heroVideoUrl, setHeroVideoUrl] = useState('');
  const [showSlogan, setShowSlogan] = useState(true);

  // 监听滚动，标语只在首页底部显示
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('.landing-hero-section');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        setShowSlogan(rect.bottom > 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 处理图片URL，相对路径转为绝对路径
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${BASE_URL}${url}`;
  };

  // 语言切换时保存到localStorage
  const handleLangChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  // 检测登录状态
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    setIsLoggedIn(!!token);
  }, []);

  // 获取注册链接（携带邀请码、活动码和可选的重定向参数）
  const getRegisterLink = (redirectPath) => {
    const referrerCode = localStorage.getItem('referrer_code');
    const campaignCode = localStorage.getItem('campaign_code');
    const params = new URLSearchParams();
    if (referrerCode) params.set('ref', referrerCode);
    if (campaignCode) params.set('campaign', campaignCode);
    if (redirectPath) params.set('redirect', redirectPath);
    const queryString = params.toString();
    return queryString ? `/register?${queryString}` : '/register';
  };

  // 获取首页配置（底部链接等）
  useEffect(() => {
    const fetchHomepageConfig = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/homepage-config`);
        const data = await response.json();
        if (data.code === 0 && data.data) {
          // 解析底部链接配置
          let developerLinks = [];
          let friendlyLinks = [];
          let companyLinks = [];

          try {
            developerLinks = JSON.parse(data.data.footer_developer_links || '[]');
          } catch (e) {}

          try {
            friendlyLinks = JSON.parse(data.data.footer_friendly_links || '[]');
          } catch (e) {}

          try {
            companyLinks = JSON.parse(data.data.footer_company_links || '[]');
          } catch (e) {}

          setFooterLinks({
            developer: developerLinks,
            friendly: friendlyLinks,
            company: companyLinks
          });
          
          // 设置版权信息
          if (data.data.footer_copyright) {
            setFooterCopyright(data.data.footer_copyright);
          }
          
          // 设置首页 Banner
          if (data.data.hero_banner) {
            setHeroBanner(data.data.hero_banner);
          }
          // 设置首页 Banner 点击链接
          if (data.data.hero_banner_link) {
            setHeroBannerLink(data.data.hero_banner_link);
          }
          // 设置首页视频 URL
          if (data.data.hero_video_url) {
            setHeroVideoUrl(data.data.hero_video_url);
          }
        }
      } catch (error) {
        console.log('获取首页配置失败:', error);
      }
    };

    fetchHomepageConfig();
  }, []);

  // 提交联系表单
  const handleContactSubmit = async (values) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('user_token');
      if (!token) {
        message.warning(t('请先登录后再提交', 'Please login first'));
        setContactModalVisible(false);
        return;
      }
      
      const response = await fetch(`${BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'contact',
          name: values.name || '',
          email: values.email,
          phone: values.phone || '',
          company: values.company || '',
          content: values.message || '',
        }),
      });
      const res = await response.json();
      
      if (res.code === 0) {
        message.success(t('提交成功，我们会尽快联系您！', 'Submitted successfully! We will contact you soon.'));
        contactForm.resetFields();
        setContactModalVisible(false);
      } else {
        message.error(t('提交失败，请稍后重试', 'Submission failed, please try again'));
      }
    } catch (error) {
      message.error(t('提交失败，请稍后重试', 'Submission failed, please try again'));
    } finally {
      setSubmitting(false);
    }
  };

  // 打字机效果的代码内容
  const getCodeContent = () => `import requests

response = requests.post(
  "https://api.example.com/v1/chat",
  headers={"Authorization": "Bearer YOUR_KEY"},
  json={
    "model": "deepseek-chat",
    "messages": [
      {"role": "user", "content": "${t('你好', 'Hello')}!"}
    ]
  }
)

print(response.json())`;

  // 打字机效果 - 打完后清屏重复
  useEffect(() => {
    const codeContent = getCodeContent();
    let currentIndex = 0;
    setTypedCode('');
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      if (currentIndex < codeContent.length) {
        setTypedCode(codeContent.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        // 打完后等待2秒，清屏重新开始
        setTimeout(() => {
          currentIndex = 0;
          setTypedCode('');
          setIsTyping(true);
        }, 2000);
      }
    }, 30);

    return () => clearInterval(typingInterval);
  }, [lang]);

  // 主要支持的模型
  const mainModels = [
    {
      id: 'deepseek',
      name: t('DeepSeek', 'DeepSeek'),
      nameZh: t('深度求索', 'DeepSeek'),
      desc: t('DeepSeek-V3、DeepSeek-R1 等系列模型', 'DeepSeek-V3, DeepSeek-R1 series models'),
      status: 'active',
      highlight: true,
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>DeepSeek</title>
          <path d="M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z" fill="#4D6BFE" />
        </svg>
      ),
    },
    {
      id: 'zhipu',
      name: t('智谱 AI', 'Zhipu AI'),
      nameZh: t('智谱', 'Zhipu'),
      desc: t('GLM-4、GLM-4-Plus、GLM-4-Air 等系列模型', 'GLM-4, GLM-4-Plus, GLM-4-Air series'),
      status: 'active',
      highlight: true,
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Zhipu</title>
          <path d="M11.991 23.503a.24.24 0 00-.244.248.24.24 0 00.244.249.24.24 0 00.245-.249.24.24 0 00-.22-.247l-.025-.001zM9.671 5.365a1.697 1.697 0 011.099 2.132l-.071.172-.016.04-.018.054c-.07.16-.104.32-.104.498-.035.71.47 1.279 1.186 1.314h.366c1.309.053 2.338 1.173 2.286 2.523-.052 1.332-1.152 2.38-2.478 2.327h-.174c-.715.018-1.274.64-1.239 1.368 0 .124.018.23.053.337.209.373.54.658.96.8.75.23 1.517-.125 1.9-.782l.018-.035c.402-.64 1.17-.96 1.92-.711.854.284 1.378 1.226 1.099 2.167a1.661 1.661 0 01-2.077 1.102 1.711 1.711 0 01-.907-.711l-.017-.035c-.2-.323-.463-.58-.851-.711l-.056-.018a1.646 1.646 0 00-1.954.746 1.66 1.66 0 01-1.065.764 1.677 1.677 0 01-1.989-1.279c-.209-.906.332-1.83 1.257-2.043a1.51 1.51 0 01.296-.035h.018c.68-.071 1.151-.622 1.116-1.333a1.307 1.307 0 00-.227-.693 2.515 2.515 0 01-.366-1.403 2.39 2.39 0 01.366-1.208c.14-.195.21-.444.227-.693.018-.71-.506-1.261-1.186-1.332l-.07-.018a1.43 1.43 0 01-.299-.07l-.05-.019a1.7 1.7 0 01-1.047-2.114 1.68 1.68 0 012.094-1.101zm-5.575 10.11c.26-.264.639-.367.994-.27.355.096.633.379.728.74.095.362-.007.748-.267 1.013-.402.41-1.053.41-1.455 0a1.062 1.062 0 010-1.482zm14.845-.294c.359-.09.738.024.992.297.254.274.344.665.237 1.025-.107.36-.396.634-.756.718-.551.128-1.1-.22-1.23-.781a1.05 1.05 0 01.757-1.26zm-.064-4.39c.314.32.49.753.49 1.206 0 .452-.176.886-.49 1.206-.315.32-.74.5-1.185.5-.444 0-.87-.18-1.184-.5a1.727 1.727 0 010-2.412 1.654 1.654 0 012.369 0zm-11.243.163c.364.484.447 1.128.218 1.691a1.665 1.665 0 01-2.188.923c-.855-.36-1.26-1.358-.907-2.228a1.68 1.68 0 011.33-1.038c.593-.08 1.183.169 1.547.652zm11.545-4.221c.368 0 .708.2.892.524.184.324.184.724 0 1.048a1.026 1.026 0 01-.892.524c-.568 0-1.03-.47-1.03-1.048 0-.579.462-1.048 1.03-1.048zm-14.358 0c.368 0 .707.2.891.524.184.324.184.724 0 1.048a1.026 1.026 0 01-.891.524c-.569 0-1.03-.47-1.03-1.048 0-.579.461-1.048 1.03-1.048zm10.031-1.475c.925 0 1.675.764 1.675 1.706s-.75 1.705-1.675 1.705-1.674-.763-1.674-1.705c0-.942.75-1.706 1.674-1.706zm-2.626-.684c.362-.082.653-.356.761-.718a1.062 1.062 0 00-.238-1.028 1.017 1.017 0 00-.996-.294c-.547.14-.881.7-.752 1.257.13.558.675.907 1.225.783zm0 16.876c.359-.087.644-.36.75-.72a1.062 1.062 0 00-.237-1.019 1.018 1.018 0 00-.985-.301 1.037 1.037 0 00-.762.717c-.108.361-.017.754.239 1.028.245.263.606.377.953.305l.043-.01zM17.19 3.5a.631.631 0 00.628-.64c0-.355-.279-.64-.628-.64a.631.631 0 00-.628.64c0 .355.28.64.628.64zm-10.38 0a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64a.631.631 0 00-.628.64c0 .355.279.64.628.64zm-5.182 7.852a.631.631 0 00-.628.64c0 .354.28.639.628.639a.63.63 0 00.627-.606l.001-.034a.62.62 0 00-.628-.64zm5.182 9.13a.631.631 0 00-.628.64c0 .355.279.64.628.64a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64zm10.38.018a.631.631 0 00-.628.64c0 .355.28.64.628.64a.631.631 0 00.628-.64c0-.355-.279-.64-.628-.64zm5.182-9.148a.631.631 0 00-.628.64c0 .354.279.639.628.639a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64zm-.384-4.992a.24.24 0 00.244-.249.24.24 0 00-.244-.249.24.24 0 00-.244.249c0 .142.122.249.244.249zM11.991.497a.24.24 0 00.245-.248A.24.24 0 0011.99 0a.24.24 0 00-.244.249c0 .133.108.236.223.247l.021.001zM2.011 6.36a.24.24 0 00.245-.249.24.24 0 00-.244-.249.24.24 0 00-.244.249.24.24 0 00.244.249zm0 11.263a.24.24 0 00-.243.248.24.24 0 00.244.249.24.24 0 00.244-.249.252.252 0 00-.244-.248zm19.995-.018a.24.24 0 00-.245.248.24.24 0 00.245.25.24.24 0 00.244-.25.252.252 0 00-.244-.248z" fill="#3859FF" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      id: 'doubao',
      name: t('字节跳动', 'ByteDance'),
      nameZh: t('豆包', 'Doubao'),
      desc: t('豆包系列大模型', 'Doubao series models'),
      status: 'active',
      highlight: true,
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Volcengine</title>
          <path d="M19.44 10.153l-2.936 11.586a.215.215 0 00.214.261h5.87a.215.215 0 00.214-.261l-2.95-11.586a.214.214 0 00-.412 0zM3.28 12.778l-2.275 8.96A.214.214 0 001.22 22h4.532a.212.212 0 00.214-.165.214.214 0 000-.097l-2.276-8.96a.214.214 0 00-.41 0z" fill="#00E5E5" />
          <path d="M7.29 5.359L3.148 21.738a.215.215 0 00.203.261h8.29a.214.214 0 00.215-.261L7.7 5.358a.214.214 0 00-.41 0z" fill="#006EFF" />
          <path d="M14.44.15a.214.214 0 00-.41 0L8.366 21.739a.214.214 0 00.214.261H19.9a.216.216 0 00.171-.078.214.214 0 00.044-.183L14.439.15z" fill="#006EFF" />
          <path d="M10.278 7.741L6.685 21.736a.214.214 0 00.214.264h7.17a.215.215 0 00.214-.264L10.688 7.741a.214.214 0 00-.41 0z" fill="#00E5E5" />
        </svg>
      ),
    },
    {
      id: 'qwen',
      name: t('阿里云', 'Alibaba Cloud'),
      nameZh: t('通义', 'Qwen'),
      desc: t('通义千问 Qwen 系列模型', 'Qwen series models'),
      status: 'active',
      highlight: true,
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Qwen</title>
          <defs>
            <linearGradient id="qwen-gradient-card" x1="0%" x2="100%" y1="0%" y2="0%">
              <stop offset="0%" stopColor="#00055F" stopOpacity=".84" />
              <stop offset="100%" stopColor="#6F69F7" stopOpacity=".84" />
            </linearGradient>
          </defs>
          <path d="M12.604 1.34c.393.69.784 1.382 1.174 2.075a.18.18 0 00.157.091h5.552c.174 0 .322.11.446.327l1.454 2.57c.19.337.24.478.024.837-.26.43-.513.864-.76 1.3l-.367.658c-.106.196-.223.28-.04.512l2.652 4.637c.172.301.111.494-.043.77-.437.785-.882 1.564-1.335 2.34-.159.272-.352.375-.68.37-.777-.016-1.552-.01-2.327.016a.099.099 0 00-.081.05 575.097 575.097 0 01-2.705 4.74c-.169.293-.38.363-.725.364-.997.003-2.002.004-3.017.002a.537.537 0 01-.465-.271l-1.335-2.323a.09.09 0 00-.083-.049H4.982c-.285.03-.553-.001-.805-.092l-1.603-2.77a.543.543 0 01-.002-.54l1.207-2.12a.198.198 0 000-.197 550.951 550.951 0 01-1.875-3.272l-.79-1.395c-.16-.31-.173-.496.095-.965.465-.813.927-1.625 1.387-2.436.132-.234.304-.334.584-.335a338.3 338.3 0 012.589-.001.124.124 0 00.107-.063l2.806-4.895a.488.488 0 01.422-.246c.524-.001 1.053 0 1.583-.006L11.704 1c.341-.003.724.032.9.34zm-3.432.403a.06.06 0 00-.052.03L6.254 6.788a.157.157 0 01-.135.078H3.253c-.056 0-.07.025-.041.074l5.81 10.156c.025.042.013.062-.034.063l-2.795.015a.218.218 0 00-.2.116l-1.32 2.31c-.044.078-.021.118.068.118l5.716.008c.046 0 .08.02.104.061l1.403 2.454c.046.081.092.082.139 0l5.006-8.76.783-1.382a.055.055 0 01.096 0l1.424 2.53a.122.122 0 00.107.062l2.763-.02a.04.04 0 00.035-.02.041.041 0 000-.04l-2.9-5.086a.108.108 0 010-.113l.293-.507 1.12-1.977c.024-.041.012-.062-.035-.062H9.2c-.059 0-.073-.026-.043-.077l1.434-2.505a.107.107 0 000-.114L9.225 1.774a.06.06 0 00-.053-.031zm6.29 8.02c.046 0 .058.02.034.06l-.832 1.465-2.613 4.585a.056.056 0 01-.05.029.058.058 0 01-.05-.029L8.498 9.841c-.02-.034-.01-.052.028-.054l.216-.012 6.722-.012z" fill="url(#qwen-gradient-card)" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      id: 'minimax',
      name: t('MiniMax', 'MiniMax'),
      nameZh: t('MiniMax', 'MiniMax'),
      desc: t('MiniMax 系列大模型', 'MiniMax series models'),
      status: 'active',
      highlight: true,
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Minimax</title>
          <defs>
            <linearGradient id="minimax-gradient-card" x1="0%" x2="100.182%" y1="50.057%" y2="50.057%">
              <stop offset="0%" stopColor="#E2167E" />
              <stop offset="100%" stopColor="#FE603C" />
            </linearGradient>
          </defs>
          <path d="M16.278 2c1.156 0 2.093.927 2.093 2.07v12.501a.74.74 0 00.744.709.74.74 0 00.743-.709V9.099a2.06 2.06 0 012.071-2.049A2.06 2.06 0 0124 9.1v6.561a.649.649 0 01-.652.645.649.649 0 01-.653-.645V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v7.472a2.037 2.037 0 01-2.048 2.026 2.037 2.037 0 01-2.048-2.026v-12.5a.785.785 0 00-.788-.753.785.785 0 00-.789.752l-.001 15.904A2.037 2.037 0 0113.441 22a2.037 2.037 0 01-2.048-2.026V18.04c0-.356.292-.645.652-.645.36 0 .652.289.652.645v1.934c0 .263.142.506.372.638.23.131.514.131.744 0a.734.734 0 00.372-.638V4.07c0-1.143.937-2.07 2.093-2.07zm-5.674 0c1.156 0 2.093.927 2.093 2.07v11.523a.648.648 0 01-.652.645.648.648 0 01-.652-.645V4.07a.785.785 0 00-.789-.78.785.785 0 00-.789.78v14.013a2.06 2.06 0 01-2.07 2.048 2.06 2.06 0 01-2.071-2.048V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v3.8a2.06 2.06 0 01-2.071 2.049A2.06 2.06 0 010 12.9v-1.378c0-.357.292-.646.652-.646.36 0 .653.29.653.646V12.9c0 .418.343.757.766.757s.766-.339.766-.757V9.099a2.06 2.06 0 012.07-2.048 2.06 2.06 0 012.071 2.048v8.984c0 .419.343.758.767.758.423 0 .766-.339.766-.758V4.07c0-1.143.937-2.07 2.093-2.07z" fill="url(#minimax-gradient-card)" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      id: 'kimi',
      name: t('Moonshot', 'Moonshot'),
      nameZh: t('Kimi', 'Kimi'),
      desc: t('Kimi 月之暗面系列模型', 'Kimi series models'),
      status: 'active',
      highlight: true,
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>MoonshotAI</title>
          <path d="M1.052 16.916l9.539 2.552a21.007 21.007 0 00.06 2.033l5.956 1.593a11.997 11.997 0 01-5.586.865l-.18-.016-.044-.004-.084-.009-.094-.01a11.605 11.605 0 01-.157-.02l-.107-.014-.11-.016a11.962 11.962 0 01-.32-.051l-.042-.008-.075-.013-.107-.02-.07-.015-.093-.019-.075-.016-.095-.02-.097-.023-.094-.022-.068-.017-.088-.022-.09-.024-.095-.025-.082-.023-.109-.03-.062-.02-.084-.025-.093-.028-.105-.034-.058-.019-.08-.026-.09-.031-.066-.024a6.293 6.293 0 01-.044-.015l-.068-.025-.101-.037-.057-.022-.08-.03-.087-.035-.088-.035-.079-.032-.095-.04-.063-.028-.063-.027a5.655 5.655 0 01-.041-.018l-.066-.03-.103-.047-.052-.024-.096-.046-.062-.03-.084-.04-.086-.044-.093-.047-.052-.027-.103-.055-.057-.03-.058-.032a6.49 6.49 0 01-.046-.026l-.094-.053-.06-.034-.051-.03-.072-.041-.082-.05-.093-.056-.052-.032-.084-.053-.061-.039-.079-.05-.07-.047-.053-.035a7.785 7.785 0 01-.054-.036l-.044-.03-.044-.03a6.066 6.066 0 01-.04-.028l-.057-.04-.076-.054-.069-.05-.074-.054-.056-.042-.076-.057-.076-.059-.086-.067-.045-.035-.064-.052-.074-.06-.089-.073-.046-.039-.046-.039a7.516 7.516 0 01-.043-.037l-.045-.04-.061-.053-.07-.062-.068-.06-.062-.058-.067-.062-.053-.05-.088-.084a13.28 13.28 0 01-.099-.097l-.029-.028-.041-.042-.069-.07-.05-.051-.05-.053a6.457 6.457 0 01-.168-.179l-.08-.088-.062-.07-.071-.08-.042-.049-.053-.062-.058-.068-.046-.056a7.175 7.175 0 01-.027-.033l-.045-.055-.066-.082-.041-.052-.05-.064-.02-.025a11.99 11.99 0 01-1.44-2.402zm-1.02-5.794l11.353 3.037a20.468 20.468 0 00-.469 2.011l10.817 2.894a12.076 12.076 0 01-1.845 2.005L.657 15.923l-.016-.046-.035-.104a11.965 11.965 0 01-.05-.153l-.007-.023a11.896 11.896 0 01-.207-.741l-.03-.126-.018-.08-.021-.097-.018-.081-.018-.09-.017-.084-.018-.094c-.026-.141-.05-.283-.071-.426l-.017-.118-.011-.083-.013-.102a12.01 12.01 0 01-.019-.161l-.005-.047a12.12 12.12 0 01-.034-2.145zm1.593-5.15l11.948 3.196c-.368.605-.705 1.231-1.01 1.875l11.295 3.022c-.142.82-.368 1.612-.668 2.365l-11.55-3.09L.124 10.26l.015-.1.008-.049.01-.067.015-.087.018-.098c.026-.148.056-.295.088-.442l.028-.124.02-.085.024-.097c.022-.09.045-.18.07-.268l.028-.102.023-.083.03-.1.025-.082.03-.096.026-.082.031-.095a11.896 11.896 0 011.01-2.232zm4.442-4.4L17.352 4.59a20.77 20.77 0 00-1.688 1.721l7.823 2.093c.267.852.442 1.744.513 2.665L2.106 5.213l.045-.065.027-.04.04-.055.046-.065.055-.076.054-.072.064-.086.05-.065.057-.073.055-.07.06-.074.055-.069.065-.077.054-.066.066-.077.053-.06.072-.082.053-.06.067-.074.054-.058.073-.078.058-.06.063-.067.168-.17.1-.098.059-.056.076-.071a12.084 12.084 0 012.272-1.677zM12.017 0h.097l.082.001.069.001.054.002.068.002.046.001.076.003.047.002.06.003.054.002.087.005.105.007.144.011.088.007.044.004.077.008.082.008.047.005.102.012.05.006.108.014.081.01.042.006.065.01.207.032.07.012.065.011.14.026.092.018.11.022.046.01.075.016.041.01L14.7.3l.042.01.065.015.049.012.071.017.096.024.112.03.113.03.113.032.05.015.07.02.078.024.073.023.05.016.05.016.076.025.099.033.102.036.048.017.064.023.093.034.11.041.116.045.1.04.047.02.06.024.041.018.063.026.04.018.057.025.11.048.1.046.074.035.075.036.06.028.092.046.091.045.102.052.053.028.049.026.046.024.06.033.041.022.052.029.088.05.106.06.087.051.057.034.053.032.096.059.088.055.098.062.036.024.064.041.084.056.04.027.062.042.062.043.023.017c.054.037.108.075.161.114l.083.06.065.048.056.043.086.065.082.064.04.03.05.041.086.069.079.065.085.071c.712.6 1.353 1.283 1.909 2.031L7.222.994l.062-.027.065-.028.081-.034.086-.035c.113-.045.227-.09.341-.131l.096-.035.093-.033.084-.03.096-.031c.087-.03.176-.058.264-.085l.091-.027.086-.025.102-.03.085-.023.1-.026L9.04.37l.09-.023.091-.022.095-.022.09-.02.098-.021.091-.02.095-.018.092-.018.1-.018.091-.016.098-.017.092-.014.097-.015.092-.013.102-.013.091-.012.105-.012.09-.01.105-.01c.093-.01.186-.018.28-.024l.106-.008.09-.005.11-.006.093-.004.1-.004.097-.002.099-.002.197-.002z" />
        </svg>
      ),
    },
  ];

  // 品牌Logo SVG (用于滚动展示)
  const brandLogos = [
    {
      name: t('月之暗面', 'MoonshotAI'),
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>MoonshotAI</title>
          <path d="M1.052 16.916l9.539 2.552a21.007 21.007 0 00.06 2.033l5.956 1.593a11.997 11.997 0 01-5.586.865l-.18-.016-.044-.004-.084-.009-.094-.01a11.605 11.605 0 01-.157-.02l-.107-.014-.11-.016a11.962 11.962 0 01-.32-.051l-.042-.008-.075-.013-.107-.02-.07-.015-.093-.019-.075-.016-.095-.02-.097-.023-.094-.022-.068-.017-.088-.022-.09-.024-.095-.025-.082-.023-.109-.03-.062-.02-.084-.025-.093-.028-.105-.034-.058-.019-.08-.026-.09-.031-.066-.024a6.293 6.293 0 01-.044-.015l-.068-.025-.101-.037-.057-.022-.08-.03-.087-.035-.088-.035-.079-.032-.095-.04-.063-.028-.063-.027a5.655 5.655 0 01-.041-.018l-.066-.03-.103-.047-.052-.024-.096-.046-.062-.03-.084-.04-.086-.044-.093-.047-.052-.027-.103-.055-.057-.03-.058-.032a6.49 6.49 0 01-.046-.026l-.094-.053-.06-.034-.051-.03-.072-.041-.082-.05-.093-.056-.052-.032-.084-.053-.061-.039-.079-.05-.07-.047-.053-.035a7.785 7.785 0 01-.054-.036l-.044-.03-.044-.03a6.066 6.066 0 01-.04-.028l-.057-.04-.076-.054-.069-.05-.074-.054-.056-.042-.076-.057-.076-.059-.086-.067-.045-.035-.064-.052-.074-.06-.089-.073-.046-.039-.046-.039a7.516 7.516 0 01-.043-.037l-.045-.04-.061-.053-.07-.062-.068-.06-.062-.058-.067-.062-.053-.05-.088-.084a13.28 13.28 0 01-.099-.097l-.029-.028-.041-.042-.069-.07-.05-.051-.05-.053a6.457 6.457 0 01-.168-.179l-.08-.088-.062-.07-.071-.08-.042-.049-.053-.062-.058-.068-.046-.056a7.175 7.175 0 01-.027-.033l-.045-.055-.066-.082-.041-.052-.05-.064-.02-.025a11.99 11.99 0 01-1.44-2.402zm-1.02-5.794l11.353 3.037a20.468 20.468 0 00-.469 2.011l10.817 2.894a12.076 12.076 0 01-1.845 2.005L.657 15.923l-.016-.046-.035-.104a11.965 11.965 0 01-.05-.153l-.007-.023a11.896 11.896 0 01-.207-.741l-.03-.126-.018-.08-.021-.097-.018-.081-.018-.09-.017-.084-.018-.094c-.026-.141-.05-.283-.071-.426l-.017-.118-.011-.083-.013-.102a12.01 12.01 0 01-.019-.161l-.005-.047a12.12 12.12 0 01-.034-2.145zm1.593-5.15l11.948 3.196c-.368.605-.705 1.231-1.01 1.875l11.295 3.022c-.142.82-.368 1.612-.668 2.365l-11.55-3.09L.124 10.26l.015-.1.008-.049.01-.067.015-.087.018-.098c.026-.148.056-.295.088-.442l.028-.124.02-.085.024-.097c.022-.09.045-.18.07-.268l.028-.102.023-.083.03-.1.025-.082.03-.096.026-.082.031-.095a11.896 11.896 0 011.01-2.232zm4.442-4.4L17.352 4.59a20.77 20.77 0 00-1.688 1.721l7.823 2.093c.267.852.442 1.744.513 2.665L2.106 5.213l.045-.065.027-.04.04-.055.046-.065.055-.076.054-.072.064-.086.05-.065.057-.073.055-.07.06-.074.055-.069.065-.077.054-.066.066-.077.053-.06.072-.082.053-.06.067-.074.054-.058.073-.078.058-.06.063-.067.168-.17.1-.098.059-.056.076-.071a12.084 12.084 0 012.272-1.677zM12.017 0h.097l.082.001.069.001.054.002.068.002.046.001.076.003.047.002.06.003.054.002.087.005.105.007.144.011.088.007.044.004.077.008.082.008.047.005.102.012.05.006.108.014.081.01.042.006.065.01.207.032.07.012.065.011.14.026.092.018.11.022.046.01.075.016.041.01L14.7.3l.042.01.065.015.049.012.071.017.096.024.112.03.113.03.113.032.05.015.07.02.078.024.073.023.05.016.05.016.076.025.099.033.102.036.048.017.064.023.093.034.11.041.116.045.1.04.047.02.06.024.041.018.063.026.04.018.057.025.11.048.1.046.074.035.075.036.06.028.092.046.091.045.102.052.053.028.049.026.046.024.06.033.041.022.052.029.088.05.106.06.087.051.057.034.053.032.096.059.088.055.098.062.036.024.064.041.084.056.04.027.062.042.062.043.023.017c.054.037.108.075.161.114l.083.06.065.048.056.043.086.065.082.064.04.03.05.041.086.069.079.065.085.071c.712.6 1.353 1.283 1.909 2.031L7.222.994l.062-.027.065-.028.081-.034.086-.035c.113-.045.227-.09.341-.131l.096-.035.093-.033.084-.03.096-.031c.087-.03.176-.058.264-.085l.091-.027.086-.025.102-.03.085-.023.1-.026L9.04.37l.09-.023.091-.022.095-.022.09-.02.098-.021.091-.02.095-.018.092-.018.1-.018.091-.016.098-.017.092-.014.097-.015.092-.013.102-.013.091-.012.105-.012.09-.01.105-.01c.093-.01.186-.018.28-.024l.106-.008.09-.005.11-.006.093-.004.1-.004.097-.002.099-.002.197-.002z" />
        </svg>
      ),
    },
    {
      name: t('OpenAI', 'OpenAI'),
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>OpenAI</title>
          <path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z" />
        </svg>
      ),
    },
    {
      name: t('Grok', 'Grok'),
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>Grok</title>
          <path d="M6.469 8.776L16.512 23h-4.464L2.005 8.776H6.47zm-.004 7.9l2.233 3.164L6.467 23H2l4.465-6.324zM22 2.582V23h-3.659V7.764L22 2.582zM22 1l-9.952 14.095-2.233-3.163L17.533 1H22z" />
        </svg>
      ),
    },
    {
      name: t('智谱 AI', 'Zhipu'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Zhipu</title>
          <path d="M11.991 23.503a.24.24 0 00-.244.248.24.24 0 00.244.249.24.24 0 00.245-.249.24.24 0 00-.22-.247l-.025-.001zM9.671 5.365a1.697 1.697 0 011.099 2.132l-.071.172-.016.04-.018.054c-.07.16-.104.32-.104.498-.035.71.47 1.279 1.186 1.314h.366c1.309.053 2.338 1.173 2.286 2.523-.052 1.332-1.152 2.38-2.478 2.327h-.174c-.715.018-1.274.64-1.239 1.368 0 .124.018.23.053.337.209.373.54.658.96.8.75.23 1.517-.125 1.9-.782l.018-.035c.402-.64 1.17-.96 1.92-.711.854.284 1.378 1.226 1.099 2.167a1.661 1.661 0 01-2.077 1.102 1.711 1.711 0 01-.907-.711l-.017-.035c-.2-.323-.463-.58-.851-.711l-.056-.018a1.646 1.646 0 00-1.954.746 1.66 1.66 0 01-1.065.764 1.677 1.677 0 01-1.989-1.279c-.209-.906.332-1.83 1.257-2.043a1.51 1.51 0 01.296-.035h.018c.68-.071 1.151-.622 1.116-1.333a1.307 1.307 0 00-.227-.693 2.515 2.515 0 01-.366-1.403 2.39 2.39 0 01.366-1.208c.14-.195.21-.444.227-.693.018-.71-.506-1.261-1.186-1.332l-.07-.018a1.43 1.43 0 01-.299-.07l-.05-.019a1.7 1.7 0 01-1.047-2.114 1.68 1.68 0 012.094-1.101zm-5.575 10.11c.26-.264.639-.367.994-.27.355.096.633.379.728.74.095.362-.007.748-.267 1.013-.402.41-1.053.41-1.455 0a1.062 1.062 0 010-1.482zm14.845-.294c.359-.09.738.024.992.297.254.274.344.665.237 1.025-.107.36-.396.634-.756.718-.551.128-1.1-.22-1.23-.781a1.05 1.05 0 01.757-1.26zm-.064-4.39c.314.32.49.753.49 1.206 0 .452-.176.886-.49 1.206-.315.32-.74.5-1.185.5-.444 0-.87-.18-1.184-.5a1.727 1.727 0 010-2.412 1.654 1.654 0 012.369 0zm-11.243.163c.364.484.447 1.128.218 1.691a1.665 1.665 0 01-2.188.923c-.855-.36-1.26-1.358-.907-2.228a1.68 1.68 0 011.33-1.038c.593-.08 1.183.169 1.547.652zm11.545-4.221c.368 0 .708.2.892.524.184.324.184.724 0 1.048a1.026 1.026 0 01-.892.524c-.568 0-1.03-.47-1.03-1.048 0-.579.462-1.048 1.03-1.048zm-14.358 0c.368 0 .707.2.891.524.184.324.184.724 0 1.048a1.026 1.026 0 01-.891.524c-.569 0-1.03-.47-1.03-1.048 0-.579.461-1.048 1.03-1.048zm10.031-1.475c.925 0 1.675.764 1.675 1.706s-.75 1.705-1.675 1.705-1.674-.763-1.674-1.705c0-.942.75-1.706 1.674-1.706zm-2.626-.684c.362-.082.653-.356.761-.718a1.062 1.062 0 00-.238-1.028 1.017 1.017 0 00-.996-.294c-.547.14-.881.7-.752 1.257.13.558.675.907 1.225.783zm0 16.876c.359-.087.644-.36.75-.72a1.062 1.062 0 00-.237-1.019 1.018 1.018 0 00-.985-.301 1.037 1.037 0 00-.762.717c-.108.361-.017.754.239 1.028.245.263.606.377.953.305l.043-.01zM17.19 3.5a.631.631 0 00.628-.64c0-.355-.279-.64-.628-.64a.631.631 0 00-.628.64c0 .355.28.64.628.64zm-10.38 0a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64a.631.631 0 00-.628.64c0 .355.279.64.628.64zm-5.182 7.852a.631.631 0 00-.628.64c0 .354.28.639.628.639a.63.63 0 00.627-.606l.001-.034a.62.62 0 00-.628-.64zm5.182 9.13a.631.631 0 00-.628.64c0 .355.279.64.628.64a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64zm10.38.018a.631.631 0 00-.628.64c0 .355.28.64.628.64a.631.631 0 00.628-.64c0-.355-.279-.64-.628-.64zm5.182-9.148a.631.631 0 00-.628.64c0 .354.279.639.628.639a.631.631 0 00.628-.64c0-.355-.28-.64-.628-.64zm-.384-4.992a.24.24 0 00.244-.249.24.24 0 00-.244-.249.24.24 0 00-.244.249c0 .142.122.249.244.249zM11.991.497a.24.24 0 00.245-.248A.24.24 0 0011.99 0a.24.24 0 00-.244.249c0 .133.108.236.223.247l.021.001zM2.011 6.36a.24.24 0 00.245-.249.24.24 0 00-.244-.249.24.24 0 00-.244.249.24.24 0 00.244.249zm0 11.263a.24.24 0 00-.243.248.24.24 0 00.244.249.24.24 0 00.244-.249.252.252 0 00-.244-.248zm19.995-.018a.24.24 0 00-.245.248.24.24 0 00.245.25.24.24 0 00.244-.25.252.252 0 00-.244-.248z" fill="#3859FF" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      name: t('火山引擎', 'Volcengine'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Volcengine</title>
          <path d="M19.44 10.153l-2.936 11.586a.215.215 0 00.214.261h5.87a.215.215 0 00.214-.261l-2.95-11.586a.214.214 0 00-.412 0zM3.28 12.778l-2.275 8.96A.214.214 0 001.22 22h4.532a.212.212 0 00.214-.165.214.214 0 000-.097l-2.276-8.96a.214.214 0 00-.41 0z" fill="#00E5E5" />
          <path d="M7.29 5.359L3.148 21.738a.215.215 0 00.203.261h8.29a.214.214 0 00.215-.261L7.7 5.358a.214.214 0 00-.41 0z" fill="#006EFF" />
          <path d="M14.44.15a.214.214 0 00-.41 0L8.366 21.739a.214.214 0 00.214.261H19.9a.216.216 0 00.171-.078.214.214 0 00.044-.183L14.439.15z" fill="#006EFF" />
          <path d="M10.278 7.741L6.685 21.736a.214.214 0 00.214.264h7.17a.215.215 0 00.214-.264L10.688 7.741a.214.214 0 00-.41 0z" fill="#00E5E5" />
        </svg>
      ),
    },
    {
      name: t('Cohere', 'Cohere'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Cohere</title>
          <path clipRule="evenodd" d="M8.128 14.099c.592 0 1.77-.033 3.398-.703 1.897-.781 5.672-2.2 8.395-3.656 1.905-1.018 2.74-2.366 2.74-4.18A4.56 4.56 0 0018.1 1H7.549A6.55 6.55 0 001 7.55c0 3.617 2.745 6.549 7.128 6.549z" fill="#39594D" fillRule="evenodd" />
          <path clipRule="evenodd" d="M9.912 18.61a4.387 4.387 0 012.705-4.052l3.323-1.38c3.361-1.394 7.06 1.076 7.06 4.715a5.104 5.104 0 01-5.105 5.104l-3.597-.001a4.386 4.386 0 01-4.386-4.387z" fill="#D18EE2" fillRule="evenodd" />
          <path d="M4.776 14.962A3.775 3.775 0 001 18.738v.489a3.776 3.776 0 007.551 0v-.49a3.775 3.775 0 00-3.775-3.775z" fill="#FF7759" />
        </svg>
      ),
    },
    {
      name: t('Claude', 'Claude'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Claude</title>
          <path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z" fill="#D97757" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      name: t('Gemini', 'Gemini'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Gemini</title>
          <defs>
            <linearGradient id="lobe-icons-gemini-fill" x1="0%" x2="68.73%" y1="100%" y2="30.395%">
              <stop offset="0%" stopColor="#1C7DFF" />
              <stop offset="52.021%" stopColor="#1C69FF" />
              <stop offset="100%" stopColor="#F0DCD6" />
            </linearGradient>
          </defs>
          <path d="M12 24A14.304 14.304 0 000 12 14.304 14.304 0 0012 0a14.305 14.305 0 0012 12 14.305 14.305 0 00-12 12" fill="url(#lobe-icons-gemini-fill)" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      name: t('Suno', 'Suno'),
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>Suno</title>
          <path d="M16.5 0C20.642 0 24 5.373 24 12h-9c0 6.627-3.358 12-7.5 12C3.358 24 0 18.627 0 12h9c0-6.627 3.358-12 7.5-12z" />
        </svg>
      ),
    },
    {
      name: t('MiniMax', 'Minimax'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Minimax</title>
          <defs>
            <linearGradient id="lobe-icons-minimax-fill" x1="0%" x2="100.182%" y1="50.057%" y2="50.057%">
              <stop offset="0%" stopColor="#E2167E" />
              <stop offset="100%" stopColor="#FE603C" />
            </linearGradient>
          </defs>
          <path d="M16.278 2c1.156 0 2.093.927 2.093 2.07v12.501a.74.74 0 00.744.709.74.74 0 00.743-.709V9.099a2.06 2.06 0 012.071-2.049A2.06 2.06 0 0124 9.1v6.561a.649.649 0 01-.652.645.649.649 0 01-.653-.645V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v7.472a2.037 2.037 0 01-2.048 2.026 2.037 2.037 0 01-2.048-2.026v-12.5a.785.785 0 00-.788-.753.785.785 0 00-.789.752l-.001 15.904A2.037 2.037 0 0113.441 22a2.037 2.037 0 01-2.048-2.026V18.04c0-.356.292-.645.652-.645.36 0 .652.289.652.645v1.934c0 .263.142.506.372.638.23.131.514.131.744 0a.734.734 0 00.372-.638V4.07c0-1.143.937-2.07 2.093-2.07zm-5.674 0c1.156 0 2.093.927 2.093 2.07v11.523a.648.648 0 01-.652.645.648.648 0 01-.652-.645V4.07a.785.785 0 00-.789-.78.785.785 0 00-.789.78v14.013a2.06 2.06 0 01-2.07 2.048 2.06 2.06 0 01-2.071-2.048V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v3.8a2.06 2.06 0 01-2.071 2.049A2.06 2.06 0 010 12.9v-1.378c0-.357.292-.646.652-.646.36 0 .653.29.653.646V12.9c0 .418.343.757.766.757s.766-.339.766-.757V9.099a2.06 2.06 0 012.07-2.048 2.06 2.06 0 012.071 2.048v8.984c0 .419.343.758.767.758.423 0 .766-.339.766-.758V4.07c0-1.143.937-2.07 2.093-2.07z" fill="url(#lobe-icons-minimax-fill)" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      name: t('DeepSeek', 'DeepSeek'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>DeepSeek</title>
          <path d="M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z" fill="#4D6BFE" />
        </svg>
      ),
    },
    {
      name: t('通义千问', 'Qwen'),
      svg: (
        <svg viewBox="0 0 24 24" width="48" height="48">
          <title>Qwen</title>
          <defs>
            <linearGradient id="lobe-icons-qwen-fill" x1="0%" x2="100%" y1="0%" y2="0%">
              <stop offset="0%" stopColor="#00055F" stopOpacity=".84" />
              <stop offset="100%" stopColor="#6F69F7" stopOpacity=".84" />
            </linearGradient>
          </defs>
          <path d="M12.604 1.34c.393.69.784 1.382 1.174 2.075a.18.18 0 00.157.091h5.552c.174 0 .322.11.446.327l1.454 2.57c.19.337.24.478.024.837-.26.43-.513.864-.76 1.3l-.367.658c-.106.196-.223.28-.04.512l2.652 4.637c.172.301.111.494-.043.77-.437.785-.882 1.564-1.335 2.34-.159.272-.352.375-.68.37-.777-.016-1.552-.01-2.327.016a.099.099 0 00-.081.05 575.097 575.097 0 01-2.705 4.74c-.169.293-.38.363-.725.364-.997.003-2.002.004-3.017.002a.537.537 0 01-.465-.271l-1.335-2.323a.09.09 0 00-.083-.049H4.982c-.285.03-.553-.001-.805-.092l-1.603-2.77a.543.543 0 01-.002-.54l1.207-2.12a.198.198 0 000-.197 550.951 550.951 0 01-1.875-3.272l-.79-1.395c-.16-.31-.173-.496.095-.965.465-.813.927-1.625 1.387-2.436.132-.234.304-.334.584-.335a338.3 338.3 0 012.589-.001.124.124 0 00.107-.063l2.806-4.895a.488.488 0 01.422-.246c.524-.001 1.053 0 1.583-.006L11.704 1c.341-.003.724.032.9.34zm-3.432.403a.06.06 0 00-.052.03L6.254 6.788a.157.157 0 01-.135.078H3.253c-.056 0-.07.025-.041.074l5.81 10.156c.025.042.013.062-.034.063l-2.795.015a.218.218 0 00-.2.116l-1.32 2.31c-.044.078-.021.118.068.118l5.716.008c.046 0 .08.02.104.061l1.403 2.454c.046.081.092.082.139 0l5.006-8.76.783-1.382a.055.055 0 01.096 0l1.424 2.53a.122.122 0 00.107.062l2.763-.02a.04.04 0 00.035-.02.041.041 0 000-.04l-2.9-5.086a.108.108 0 010-.113l.293-.507 1.12-1.977c.024-.041.012-.062-.035-.062H9.2c-.059 0-.073-.026-.043-.077l1.434-2.505a.107.107 0 000-.114L9.225 1.774a.06.06 0 00-.053-.031zm6.29 8.02c.046 0 .058.02.034.06l-.832 1.465-2.613 4.585a.056.056 0 01-.05.029.058.058 0 01-.05-.029L8.498 9.841c-.02-.034-.01-.052.028-.054l.216-.012 6.722-.012z" fill="url(#lobe-icons-qwen-fill)" fillRule="nonzero" />
        </svg>
      ),
    },
    {
      name: t('Midjourney', 'Midjourney'),
      svg: (
        <svg fill="currentColor" viewBox="0 0 24 24" width="48" height="48">
          <title>Midjourney</title>
          <path d="M22.369 17.676c-1.387 1.259-3.17 2.378-5.332 3.417.044.03.086.057.13.083l.018.01.019.012c.216.123.42.184.641.184.222 0 .426-.061.642-.184l.018-.011.019-.011c.14-.084.266-.178.492-.366l.178-.148c.279-.232.426-.342.625-.456.304-.174.612-.266.949-.266.337 0 .645.092.949.266l.023.014c.188.109.334.219.602.442l.178.148c.221.184.346.278.483.36l.028.017.018.01c.21.12.407.181.62.185h.022a.31.31 0 110 .618c-.337 0-.645-.092-.95-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.02-.014a5.356 5.356 0 01-.49-.377l-.159-.132a3.836 3.836 0 00-.483-.36l-.027-.017-.019-.01a1.256 1.256 0 00-.641-.185c-.222 0-.426.061-.641.184l-.02.011-.018.011c-.14.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.51.39l-.022.014-.022.014-.09.054a1.868 1.868 0 01-.95.266c-.337 0-.644-.092-.949-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.026-.017a4.881 4.881 0 01-.425-.325.308.308 0 01-.12-.1l-.098-.081a3.836 3.836 0 00-.483-.36l-.027-.017-.019-.01a1.256 1.256 0 00-.641-.185c-.222 0-.426.061-.642.184l-.018.011-.019.011c-.14.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.51.39l-.023.014-.022.014-.09.054A1.868 1.868 0 0112 22c-.337 0-.645-.092-.949-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.021-.014a5.356 5.356 0 01-.49-.377l-.158-.132a3.836 3.836 0 00-.483-.36l-.028-.017-.018-.01a1.256 1.256 0 00-.642-.185c-.221 0-.425.061-.641.184l-.019.011-.018.011c-.141.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.511.39l-.022.014-.022.014-.09.054a1.868 1.868 0 01-.986.264c-.746-.09-1.319-.38-1.89-.866l-.035-.03c-.047-.041-.118-.106-.192-.174l-.196-.181-.107-.1-.011-.01a1.531 1.531 0 00-.336-.253.313.313 0 00-.095-.03h-.005c-.119.022-.238.059-.361.11a.308.308 0 01-.077.061l-.008.005a.309.309 0 01-.126.034 5.66 5.66 0 00-.774.518l-.416.324-.055.043a6.542 6.542 0 01-.324.236c-.305.207-.552.315-.8.315a.31.31 0 01-.01-.618h.01c.09 0 .235-.062.438-.198l.04-.027c.077-.054.163-.117.27-.199l.385-.301.06-.047c.268-.206.506-.373.73-.505l-.633-1.21a.309.309 0 01.254-.451l20.287-1.305a.309.309 0 01.228.537zm-1.118.14L2.369 19.03l.423.809c.128-.045.256-.078.388-.1a.31.31 0 01.052-.005c.132 0 .26.032.386.093.153.073.294.179.483.35l.016.015.092.086.144.134.097.089c.065.06.125.114.16.144.485.418.948.658 1.554.736h.011a1.25 1.25 0 00.6-.172l.021-.011.019-.011.018-.011c.141-.084.266-.178.492-.366l.178-.148c.279-.232.426-.342.625-.456.305-.174.612-.266.95-.266.336 0 .644.092.948.266l.023.014c.188.109.335.219.603.442l.177.148c.222.184.346.278.484.36l.027.017.019.01c.215.124.42.185.641.185.222 0 .426-.061.641-.184l.019-.011.018-.011c.141-.084.267-.178.493-.366l.177-.148c.28-.232.427-.342.626-.456.304-.174.612-.266.949-.266.337 0 .644.092.949.266l.025.015c.187.109.334.22.603.443 1.867-.878 3.448-1.811 4.73-2.832l.02-.016zM3.653 2.026C6.073 3.06 8.69 4.941 10.8 7.258c2.46 2.7 4.109 5.828 4.637 9.149a.31.31 0 01-.421.335c-2.348-.945-4.54-1.258-6.59-1.02-1.739.2-3.337.792-4.816 1.703-.294.182-.62-.182-.405-.454 1.856-2.355 2.581-4.99 2.343-7.794-.195-2.292-1.031-4.61-2.284-6.709a.31.31 0 01.388-.442zM10.04 4.45c1.778.543 3.892 2.102 5.782 4.243 1.984 2.248 3.552 4.934 4.347 7.582a.31.31 0 01-.401.38l-.022-.01-.386-.154a10.594 10.594 0 00-.291-.112l-.016-.006c-.68-.247-1.199-.291-1.944-.101a.31.31 0 01-.375-.218C15.378 11.123 13.073 7.276 9.775 5c-.291-.201-.072-.653.266-.55zM4.273 2.996l.008.015c1.028 1.94 1.708 4.031 1.885 6.113.213 2.513-.31 4.906-1.673 7.092l-.02.031.003-.001c1.198-.581 2.47-.969 3.825-1.132l.055-.006c1.981-.23 4.083.029 6.309.837l.066.025-.007-.039c-.593-2.95-2.108-5.737-4.31-8.179l-.07-.078c-1.785-1.96-3.944-3.6-6.014-4.65l-.057-.028zm7.92 3.238l.048.048c2.237 2.295 3.885 5.431 4.974 9.191l.038.132.022-.004c.71-.133 1.284-.063 1.963.18l.027.01.066.024.046.018-.025-.073c-.811-2.307-2.208-4.62-3.936-6.594l-.058-.065c-1.02-1.155-2.103-2.132-3.15-2.856l-.015-.011z" />
        </svg>
      ),
    },
  ];

  // 核心优势
  const advantages = [
    {
      icon: <SafetyOutlined />,
      title: t('稳定可靠', 'Stable & Reliable'),
      desc: t('99.9% 服务可用性，智能负载均衡', '99.9% uptime, smart load balancing'),
    },
    {
      icon: <ThunderboltOutlined />,
      title: t('安全合规', 'Secure & Compliant'),
      desc: t('数据加密传输，符合国内安全标准', 'Encrypted transmission, compliant with standards'),
    },
    {
      icon: <DollarOutlined />,
      title: t('价格实惠', 'Affordable Pricing'),
      desc: t('低于官方价格，按量付费无门槛', 'Lower than official, pay as you go'),
    },
    {
      icon: <CustomerServiceOutlined />,
      title: t('7×24 支持', '24/7 Support'),
      desc: t('全天候在线，快速响应解决问题', '24/7 online, fast problem solving'),
    },
  ];

  return (
    <div className="landing">
      {/* 导航栏 */}
      <nav className="landing-nav">
        <div className="landing-nav-container">
          <Link to="/" className="landing-logo">
            <img src={require('../utils/img/logo.png')} alt="Blue API" />
            <span>Blue API</span>
          </Link>
          <div className="landing-nav-links">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>{t('首页', 'Home')}</a>
            <a href="#models">{t('支持模型', 'Models')}</a>
            <Link to="/pricing">{t('定价', 'Pricing')}</Link>
            <a href="#features">{t('功能特性', 'Features')}</a>
            <Link to="/contact">{t('商务合作', 'Business')}</Link>
            <Link to="/about">{t('关于我们', 'About Us')}</Link>
            <div className="landing-lang-switch">
              <div className={`lang-slider ${lang === 'en' ? 'en' : ''}`}></div>
              <span
                className={lang === 'zh' ? 'active' : ''}
                onClick={() => handleLangChange('zh')}
              >中</span>
              <span
                className={lang === 'en' ? 'active' : ''}
                onClick={() => handleLangChange('en')}
              >EN</span>
            </div>
            {isLoggedIn ? (
              <Link to="/dashboard" className="landing-nav-register">{t('控制台', 'Dashboard')}</Link>
            ) : (
              <>
                <Link to="/login" className="landing-nav-login">{t('登录', 'Login')}</Link>
                <Link to={getRegisterLink()} className="landing-nav-register">{t('免费注册', 'Sign Up')}</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero 区域 */}
      <section className="landing-hero-section">
        {heroBanner && (
          <div className="landing-hero-banner">
            {heroBannerLink ? (
              <a href={heroBannerLink} target="_blank" rel="noopener noreferrer">
                <img src={getFullImageUrl(heroBanner)} alt="Banner" />
              </a>
            ) : (
              <img src={getFullImageUrl(heroBanner)} alt="Banner" />
            )}
          </div>
        )}
        <div className="landing-hero">
          <div className="landing-hero-content">
            <div className="landing-hero-badge">
              <span className="rocket-icon">🚀</span> {t('国内主流 AI 接口聚合服务', 'China\'s Leading AI API Aggregator')}
            </div>
          <h1>{t(
            '稳定、安全、实惠的聚合 AI 接口',
            'Stable, Affordable AI APIs'
          )}</h1>
          <p>
            {t(
              '一站式接入国内主流 AI 模型。按量付费，价格实惠，7×24 在线技术支持。',
              'One-step access to China\'s leading AI models. Pay as you need, affordable pricing, 24/7 technical support.'
            )}
          </p>
          <div className="landing-hero-actions">
            <Link to={getRegisterLink()} className="landing-btn-primary">
              {t('获取API KEY', 'GET API KEY')}
            </Link>
            <a href="#models" className="landing-btn-secondary">
              {t('查看模型', 'View Models')}
            </a>
          </div>
          <div className="landing-hero-stats">
            <div className="landing-stat">
              <div className="landing-stat-value landing-stat-badge">60+</div>
              <div className="landing-stat-label">{t('主流模型', 'Leading Models')}</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat-value">99.9%</div>
              <div className="landing-stat-label">{t('可用性', 'Uptime')}</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat-value">7×24</div>
              <div className="landing-stat-label">{t('在线支持', 'Support')}</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat-value">20+</div>
              <div className="landing-stat-label">{t('服务商', 'Providers')}</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat-value">3000+</div>
              <div className="landing-stat-label">{t('全球开发者', 'Developers')}</div>
            </div>
          </div>
          </div>
          <div className="landing-hero-visual">
            {heroVideoUrl ? (
              <div className="landing-video-preview">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  style={{ width: '100%', borderRadius: 12, display: 'block' }}
                  src={heroVideoUrl}
                />
              </div>
            ) : (
              <div className="landing-code-preview">
                <div className="landing-code-header">
                  <span className="landing-code-dot"></span>
                  <span className="landing-code-dot"></span>
                  <span className="landing-code-dot"></span>
                  <span className="landing-code-title">{t('快速开始', 'Quick Start')}</span>
                </div>
                <pre className="landing-code-content">
                  <div className="landing-code-line-numbers">
                    {typedCode.split('\n').map((_, index) => (
                      <span key={index} className="landing-code-line-number">{index + 1}</span>
                    ))}
                  </div>
                  <code>{typedCode}{isTyping && <span className="typing-cursor">|</span>}</code>
                </pre>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 支持的模型 */}
      <section id="models" className="landing-models">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2>{t('支持的 AI 模型', 'Supported AI Models')}</h2>
            <p>{t(
              '接入国内主流 AI 大模型，API 统一兼容，随时切换',
              'Access China\'s leading AI models with unified API, switch anytime'
            )}</p>
          </div>
          <div className="landing-models-grid-new">
            {mainModels.map((model) => (
              <div
                key={model.id}
                className={`landing-model-card-new ${model.highlight ? 'highlight' : ''} ${model.status}`}
              >
                <div className="landing-model-header">
                  <div className="landing-model-icon">
                    {model.svg}
                  </div>
                  <div className="landing-model-info">
                    <div className="landing-model-name">{model.name}</div>
                    <div className="landing-model-namezh">{model.nameZh}</div>
                  </div>
                  {model.status === 'active' && (
                    <span className="landing-model-status active">
                      <StarFilled /> {t('已接入', 'Active')}
                    </span>
                  )}
                  {model.status === 'coming' && (
                    <span className="landing-model-status coming">
                      {t('即将接入', 'Coming Soon')}
                    </span>
                  )}
                </div>
                <div className="landing-model-desc">{model.desc}</div>
              </div>
            ))}
          </div>
        </div>
          <div className="landing-models-notice">
          {t('更多模型持续接入中...', 'More models coming soon...')}
        </div>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              if (isLoggedIn) {
                window.location.href = '/dashboard/models';
              } else {
                // 未登录，跳转到登录页并带上 redirect 参数
                window.location.href = '/login?redirect=/dashboard/models';
              }
            }}
            style={{ height: 48, paddingLeft: 48, paddingRight: 48, fontSize: 16 }}
          >
            {t('探索全部模型 →', 'Explore All Models →')}
          </Button>
        </div>
      </section>

      {/* 功能特性 + 定价 合并 */}
      <section id="features" className="landing-features">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2>{t('为什么选择我们', 'Why Choose Us')}</h2>
            <p>{t(
              '专注国内 AI 接口服务，简单、稳定、实惠',
              'Focused on China\'s AI APIs - Simple, Stable, Affordable'
            )}</p>
          </div>
          
          {/* 核心优势 */}
          <div className="landing-advantages-grid">
            {advantages.map((item, index) => (
              <div key={index} className="landing-advantage-card">
                <div className="landing-advantage-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

        </div>

        {/* 滚动品牌 Logo - Trusted by Industry Leaders */}
        <div className="landing-trusted-section">
          <div className="landing-trusted-title">
            {t('业界领先品牌信赖', 'Trusted by World AI Leaders')}
          </div>
          <div className="landing-models-scroll-wrapper">
            <div className="landing-scroll-fade-left"></div>
            <div className="landing-scroll-fade-right"></div>
            <div className="landing-models-scroll">
              <div className="landing-scroll-logos">
                {[...brandLogos, ...brandLogos].map((brand, index) => (
                  <div key={index} className="landing-scroll-logo-item" title={brand.name}>
                    {brand.svg}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 服务承诺 */}
      <section className="landing-service">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2>{t('服务承诺', 'Our Commitment')}</h2>
          </div>
          <div className="landing-service-grid">
            <div className="landing-service-item">
              <div className="landing-service-icon">
                <ClockCircleOutlined />
              </div>
              <h4>{t('快速响应', 'Fast Response')}</h4>
              <p>{t('平均响应时间 < 2 小时', 'Average response < 2 hours')}</p>
            </div>
            <div className="landing-service-item">
              <div className="landing-service-icon">
                <CodeOutlined />
              </div>
              <h4>{t('问题定位', 'Problem Diagnosis')}</h4>
              <p>{t('精准定位问题，快速给出解决方案', 'Precise diagnosis, quick solutions')}</p>
            </div>
            <div className="landing-service-item">
              <div className="landing-service-icon">
                <CustomerServiceOutlined />
              </div>
              <h4>{t('全天候支持', '24/7 Support')}</h4>
              <p>{t('7×24 小时在线技术支持', '24/7 online technical support')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 使用步骤 */}
      <section id="steps" className="landing-steps">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2>{t('三步开始使用', 'Get Started in 3 Steps')}</h2>
          </div>
          <div className="landing-steps-grid">
            <div className="landing-step-item">
              <div className="landing-step-number">1</div>
              <h4>{t('注册账号', 'Sign Up')}</h4>
              <p>{t('免费注册，获取 API Key', 'Free signup, get your API key')}</p>
            </div>
            <div className="landing-step-arrow">→</div>
            <div className="landing-step-item">
              <div className="landing-step-number">2</div>
              <h4>{t('充值余额', 'Top Up')}</h4>
              <p>{t('按需充值，无最低限制', 'Top up as needed, no minimum')}</p>
            </div>
            <div className="landing-step-arrow">→</div>
            <div className="landing-step-item">
              <div className="landing-step-number">3</div>
              <h4>{t('开始调用', 'Start Calling')}</h4>
              <p>{t('接入 API，立即开始使用', 'Integrate API, start instantly')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <div className="landing-section-container">
          <h2>{t('准备好开始了吗？', 'Ready to Get Started?')}</h2>
          <p>{t(
            '注册即获赠免费体验额度，立即开始您的 AI 之旅',
            'Sign up and get free credits to start your AI journey'
          )}</p>
          <Link to={getRegisterLink()} className="landing-btn-primary large">
            {t('免费注册，立即体验', 'Sign Up Free')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-container">
          <div className="landing-footer-grid">
            <div className="landing-footer-brand">
              <Link to="/" className="landing-footer-logo">
                <img src={require('../utils/img/logo.png')} alt="Blue API" />
                <span>Blue API</span>
              </Link>
              <p className="landing-footer-slogan">{t('一个接口，连接所有 AI', 'One API, Connect All AI')}</p>
              <p className="landing-footer-subtitle">{t('让 AI 触手可及', 'Making AI Accessible')}</p>
            </div>
            <div className="landing-footer-column">
              <h4>{t('产品', 'Product')}</h4>
              <a href="#models">{t('支持模型', 'Models')}</a>
              <a href="#features">{t('功能特性', 'Features')}</a>
              <Link to="/pricing">{t('定价', 'Pricing')}</Link>
            </div>
            <div className="landing-footer-column">
              <h4>{t('开发者', 'Developer')}</h4>
              {footerLinks.developer.length > 0 ? (
                footerLinks.developer.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.url} 
                    target={link.url !== '#' ? '_blank' : '_self'} 
                    rel="noopener noreferrer"
                  >
                    {link.title}
                  </a>
                ))
              ) : (
                <>
                  <a href="#">{t('API 文档', 'API Docs')}</a>
                  <a href="#">{t('开发指南', 'Dev Guide')}</a>
                  <a href="#">{t('SDK 下载', 'SDK')}</a>
                </>
              )}
            </div>
            <div className="landing-footer-column">
              <h4>{t('公司', 'Company')}</h4>
              {footerLinks.company.length > 0 ? (
                footerLinks.company.map((link, index) => (
                  <Link
                    key={index}
                    to={link.url}
                  >
                    {link.title}
                  </Link>
                ))
              ) : (
                <>
                  <Link to="/about">{t('关于我们', 'About Us')}</Link>
                  <Link to="/contact">{t('联系我们', 'Contact')}</Link>
                  <Link to="/terms">{t('服务条款', 'Terms')}</Link>
                  <Link to="/privacy">{t('隐私政策', 'Privacy')}</Link>
                </>
              )}
            </div>
            <div className="landing-footer-column">
              <h4>{t('友情链接', 'Links')}</h4>
              {footerLinks.friendly.length > 0 ? (
                footerLinks.friendly.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {link.title}
                  </a>
                ))
              ) : (
                <>
                  <a href="https://www.deepseek.com/" target="_blank" rel="noopener noreferrer">DeepSeek</a>
                  <a href="https://www.zhipuai.cn/" target="_blank" rel="noopener noreferrer">{t('智谱AI', 'Zhipu AI')}</a>
                  <a href="https://www.doubao.com/" target="_blank" rel="noopener noreferrer">{t('豆包', 'Doubao')}</a>
                  <a href="https://tongyi.aliyun.com/" target="_blank" rel="noopener noreferrer">{t('通义千问', 'Tongyi')}</a>
                </>
              )}
            </div>
          </div>
          <div className="landing-footer-bottom">
            <p>{footerCopyright}</p>
          </div>
        </div>
      </footer>

      {/* 联系我们弹窗 */}
      <Modal
        title={t('商务合作', 'Business Cooperation')}
        open={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={null}
        width={460}
        centered
        maskClosable={true}
        styles={{
          body: { paddingTop: 8, paddingBottom: 8 },
          wrapper: { overflow: 'hidden' }
        }}
      >
        <Form
          form={contactForm}
          layout="vertical"
          onFinish={handleContactSubmit}
          requiredMark={false}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="name"
            label={<span>您的姓名 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            rules={[{ required: true, message: t('请输入姓名', 'Please enter your name') }]}
            style={{ marginBottom: 24 }}
          >
            <Input placeholder={t('请输入您的姓名', 'Please enter your name')} />
          </Form.Item>
          <Form.Item
            name="email"
            label={<span>电子邮箱 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            rules={[
              { required: true, message: t('请输入邮箱', 'Please enter your email') },
              { type: 'email', message: t('请输入有效的邮箱地址', 'Please enter a valid email') }
            ]}
            style={{ marginBottom: 24 }}
          >
            <Input placeholder={t('请输入您的邮箱', 'Please enter your email')} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="联系电话"
            style={{ marginBottom: 24 }}
          >
            <Input placeholder={t('请输入您的联系电话', 'Please enter your phone number')} />
          </Form.Item>
          <Form.Item
            name="company"
            label="公司名称"
            style={{ marginBottom: 24 }}
          >
            <Input placeholder={t('请输入公司名称（选填）', 'Please enter company name (optional)')} />
          </Form.Item>
          <Form.Item
            name="message"
            label={<span>需求描述 <span style={{ color: '#ff4d4f' }}>*</span></span>}
            rules={[{ required: true, message: t('请描述您的需求', 'Please describe your needs') }]}
            style={{ marginBottom: 28 }}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('请简要描述您的需求或问题', 'Please briefly describe your needs or questions')}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <button type="button" className="landing-btn-secondary" style={{ marginRight: 12, border: 'none' }} onClick={() => setContactModalVisible(false)}>
              {t('取消', 'Cancel')}
            </button>
            <button type="submit" className="landing-btn-primary" style={{ border: 'none' }}>
              {t('提交', 'Submit')}
            </button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 在线客服悬浮按钮 */}
      <CustomerService />

      <style>{`
        .landing-stat-badge {
          color: #1890ff;
        }
        html {
          overflow-y: scroll !important;
        }
        body {
          overflow-y: scroll !important;
          scrollbar-gutter: stable !important;
          padding-right: 0 !important;
          margin-right: 0 !important;
        }
        body.ant-modal-open {
          overflow: hidden !important;
          padding-right: 0 !important;
          margin-right: 0 !important;
        }
        .landing {
          min-height: 100vh;
          overflow-x: hidden;
          width: 100%;
        }
        .ant-modal-fixed {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
        /* 强制固定宽度防止 Modal 导致宽度变化 */
        #root {
          width: 100%;
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};

export default Landing;
